import { FC, Fragment, useRef, useState, useEffect } from "react";
import { IoCloudUploadOutline, IoTrash, IoCheckmarkCircle, IoCloseCircle, IoTime } from 'react-icons/io5';

type FileResult = {
    status: 'success' | 'error' | 'pending';
    message?: string;
};

type FileUploadProps = {
    id?: string;
    name: string;
    accept: string;
    label: string;
    canRemove?: boolean;
    errorMessage: string;
    info?: string;
    onUploadFile: (file: File) => void;
    onRemoveFile?: () => void;
    disabled?: boolean;
    multiple?: boolean;
    fileResults?: Record<string, FileResult>;
    files?: File[];
}

const FileUpload: FC<FileUploadProps> = ({ onUploadFile, onRemoveFile = () => { }, accept = "*",
    canRemove = true, errorMessage, info, label, id = "dropfile", name, disabled = false, multiple = false, fileResults, files: externalFiles }) => {

    const [internalFiles, setInternalFiles] = useState<File[]>([]);
    const uploadedFiles = externalFiles || internalFiles;
    const setUploadedFiles = (updater: File[] | ((prev: File[]) => File[])) => {
        setInternalFiles(updater);
    };
    const [isDragActive, setIsDragActive] = useState(false);

    const error = errorMessage ? true : false;
    const borderColor = error ? "border-red-600" : "border-gray-300";

    useEffect(() => {
        const preventDefaults = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };

        window.addEventListener('dragover', preventDefaults);
        window.addEventListener('drop', preventDefaults);

        return () => {
            window.removeEventListener('dragover', preventDefaults);
            window.removeEventListener('drop', preventDefaults);
        };
    }, []);

    function handleDrop(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        
        if (!disabled && e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
            const newFiles = Array.from(e.dataTransfer.files) as File[];
            if (multiple) {
                setUploadedFiles(prev => [...prev, ...newFiles]);
                newFiles.forEach((file: File) => {
                    onUploadFile(file);
                });
            } else {
                const file = newFiles[0];
                if (file) {
                    setUploadedFiles([file]);
                    onUploadFile(file);
                }
            }
        }
    }

    function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    }

    function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragActive(true);
        }
    }

    function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragActive(true);
        }
    }

    const changeFile = (e: any) => {
        const newFiles = Array.from(e.target.files) as File[];
        if (multiple) {
            setUploadedFiles(prev => [...prev, ...newFiles]);
            newFiles.forEach((file: File) => {
                onUploadFile(file);
            });
        } else {
            const file = newFiles[0];
            if (file) {
                setUploadedFiles([file]);
                onUploadFile(file);
            }
        }
    }

    const removeFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
        if (uploadedFiles.length === 1) {
            onRemoveFile();
        }
    }

    const removeAllFiles = () => {
        setUploadedFiles([]);
        onRemoveFile();
    }

    return (
        <Fragment>
            <div className="space-y-2">
                {!disabled && (
                <div className="flex items-center justify-center w-full"
                    onDragEnter={handleDragEnter}
                    onDrop={handleDrop}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                >
                    <label htmlFor={id}
                        className={`${isDragActive ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20" : borderColor} 
                    flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors`}
                    >
                        <div
                            className="flex flex-col items-center justify-center pt-5 pb-6"
                        >
                            <IoCloudUploadOutline className="text-4xl text-gray-500 " />
                            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-semibold">Clique aqui para subir {multiple ? 'arquivos' : 'arquivo'}</span> ou arraste e solte aqui.
                            </p>
                            {info ? <p className="text-xs text-gray-500 dark:text-gray-400">{info}</p> : null}
                        </div>
                        <input id={id} type="file" className="hidden"
                            disabled={disabled}
                            name={name}
                            accept={accept}
                            multiple={multiple}
                            onChange={changeFile} />
                    </label>
                </div>
                )}
                {error && <p className="mt-2 text-sm text-red-600">{errorMessage}</p>}

                {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                        {multiple && (
                            <div className="flex justify-between items-center">
                                <span className="font-medium text-gray-900 dark:text-gray-100">{label} ({uploadedFiles.length})</span>
                                {canRemove && (
                                    <button
                                        type='button'
                                        className="text-red-500 cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={removeAllFiles}
                                        disabled={disabled}
                                    >
                                        Remover todos
                                    </button>
                                )}
                            </div>
                        )}
                        <div className="overflow-x-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                    <tr>
                                        <th className="py-2 px-3">#</th>
                                        <th className="py-2 px-3">Nome do arquivo</th>
                                        <th className="py-2 px-3">Resultado</th>
                                        <th className="py-2 px-3 text-right">Tamanho</th>
                                        {canRemove && <th className="py-2 px-3 text-center w-20">Ações</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {uploadedFiles.map((file, index) => (
                                        <tr key={index} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700/50'} border-b border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-slate-600`}>
                                            <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{index + 1}</td>
                                            <td className="py-2 px-3 text-gray-800 dark:text-gray-200">{file.name}</td>
                                            <td className="py-2 px-3">
                                                {fileResults ? (() => {
                                                    const result = fileResults[file.name];
                                                    if (!result || result.status === 'pending') return (
                                                        <span className="text-gray-400 dark:text-gray-500"><IoTime className="inline w-4 h-4 mr-1" />Aguardando</span>
                                                    );
                                                    if (result.status === 'success') return (
                                                        <span className="text-green-600 dark:text-green-400"><IoCheckmarkCircle className="inline w-4 h-4 mr-1" />{result.message || 'Sucesso'}</span>
                                                    );
                                                    return (
                                                        <span className="text-red-600 dark:text-red-400"><IoCloseCircle className="inline w-4 h-4 mr-1" />{result.message || 'Erro'}</span>
                                                    );
                                                })() : (
                                                    <span className="text-gray-400 dark:text-gray-500">—</span>
                                                )}
                                            </td>
                                            <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{(file.size / 1024).toFixed(1)} KB</td>
                                            {canRemove && (
                                                <td className="py-2 px-3 text-center">
                                                    <button
                                                        type='button'
                                                        onClick={() => removeFile(index)}
                                                        disabled={disabled}
                                                        className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white font-bold py-1 px-2 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Remover arquivo"
                                                    >
                                                        <IoTrash />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );

};

export default FileUpload;